// src/features/clanEvents/ClanEventsPage.tsx
import { useState } from 'react';
import { useGuild } from '../../lib/GuildContext';
import { formatGuildName } from '../../lib/formatGuildName';
import {
  useClanEvents, useUpdateClanEvent, useCancelClanEvent, useEventSnapshot,
  type ClanEvent, type ClanEventStatus,
} from './useClanEvents';
import { ChannelPicker } from '../../components/pickers/ChannelPicker';
import { formatRelativeEither, formatExactDateTime } from '../../lib/formatTime';
import './ClanEventsPage.css';

function statusBadge(status: ClanEventStatus) {
  switch (status) {
    case 'active':
    case 'ending':
      return <span className="status-badge status-badge--fresh">Active</span>;
    case 'scheduled':
      return <span className="status-badge status-badge--warning">Planned</span>;
    case 'ended':
      return <span className="status-badge status-badge--fresh" style={{ opacity: 0.6 }}>Finished</span>;
    case 'cancelled':
      return <span className="status-badge status-badge--error">Cancelled</span>;
  }
}

function EventEditForm({ event, guildId, onDone }: { event: ClanEvent; guildId: string | null; onDone: () => void }) {
  const update = useUpdateClanEvent(guildId);
  const isLive = event.status === 'active' || event.status === 'ending';

  const [clanName, setClanName] = useState(event.clan_name);
  const [channelId, setChannelId] = useState(event.channel_id);
  const [skillsText, setSkillsText] = useState((event.skills ?? []).join(', '));
  const [categoriesText, setCategoriesText] = useState((event.categories ?? []).join(', '));
  const [prizes, setPrizes] = useState(event.prize_pool ?? []);
  const [raffleEnabled, setRaffleEnabled] = useState(event.raffle_enabled);
  const [announceChange, setAnnounceChange] = useState(false);

  function addPrize() {
    setPrizes([...prizes, { place: prizes.length + 1, prize: '' }]);
  }
  function removePrize(index: number) {
    setPrizes(prizes.filter((_, i) => i !== index));
  }
  function updatePrize(index: number, field: 'place' | 'prize', value: string) {
    setPrizes(prizes.map((p, i) => i === index ? { ...p, [field]: field === 'place' ? Number(value) : value } : p));
  }

  function handleSave() {
    const payload: Record<string, unknown> = {
      channel_id: channelId,
      prize_pool: prizes,
      raffle_enabled: raffleEnabled,
    };
    // Locked fields only sent for a still-scheduled event - the
    // server would reject them anyway for an active one, but no
    // reason to even attempt it from a UI that already knows better.
    if (!isLive) {
      payload.clan_name = clanName;
      payload.skills = skillsText.split(',').map((s) => s.trim()).filter(Boolean);
      payload.categories = categoriesText.split(',').map((s) => s.trim()).filter(Boolean);
    }
    if (isLive) payload.announceChange = announceChange;

    update.mutate({ eventId: event.id, payload }, { onSuccess: onDone });
  }

  return (
    <div className="clan-event-edit">
      <div className="clan-event-edit__field">
        <label>Clan name</label>
        <input className="input" value={clanName} onChange={(e) => setClanName(e.target.value)} disabled={isLive} />
      </div>
      <div className="clan-event-edit__field">
        <label>Channel ID</label>
        <ChannelPicker guildId={guildId} value={channelId} onChange={setChannelId} />
      </div>
      {event.type !== 'boss' && (
        <div className="clan-event-edit__field">
          <label>Skills tracked (comma-separated, blank = all)</label>
          <input className="input" value={skillsText} onChange={(e) => setSkillsText(e.target.value)} disabled={isLive} />
        </div>
      )}
      {event.type !== 'xp' && (
        <div className="clan-event-edit__field">
          <label>Boss categories (comma-separated)</label>
          <input className="input" value={categoriesText} onChange={(e) => setCategoriesText(e.target.value)} disabled={isLive} />
        </div>
      )}

      <div className="clan-event-edit__field">
        <label>Prizes</label>
        {prizes.map((p, i) => (
          <div key={i} className="clan-event-edit__prize-row">
            <input className="input" type="number" min={1} style={{ width: 60 }} value={p.place} onChange={(e) => updatePrize(i, 'place', e.target.value)} />
            <input className="input" value={p.prize} onChange={(e) => updatePrize(i, 'prize', e.target.value)} placeholder="Prize description" />
            <button type="button" className="button-danger" onClick={() => removePrize(i)}>Remove</button>
          </div>
        ))}
        <button type="button" className="button-secondary" onClick={addPrize}>Add prize</button>
      </div>

      <div className="clan-event-edit__field">
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input type="checkbox" checked={raffleEnabled} onChange={(e) => setRaffleEnabled(e.target.checked)} />
          Raffle enabled
        </label>
      </div>

      {isLive && (
        <div className="clan-event-edit__field">
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" checked={announceChange} onChange={(e) => setAnnounceChange(e.target.checked)} />
            Announce this change in the event&apos;s channel
          </label>
        </div>
      )}

      {isLive && (
        <p style={{ color: 'var(--text-muted)', fontSize: 11, margin: '0.5rem 0 0' }}>
          This event has already started — clan name, skills, and boss categories are locked to keep
          the running results consistent.
        </p>
      )}

      <div className="clan-event-edit__actions">
        <button className="button-primary" onClick={handleSave} disabled={update.isPending}>
          {update.isPending ? 'Saving…' : 'Save changes'}
        </button>
        <button className="button-secondary" onClick={onDone}>Cancel</button>
        {update.isError && <span style={{ color: 'var(--negative)', fontSize: 13 }}>Couldn&apos;t save. Try again.</span>}
      </div>
    </div>
  );
}

function EventCard({ event, guildId }: { event: ClanEvent; guildId: string | null }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const cancel = useCancelClanEvent(guildId);

  const canCancel = event.status === 'scheduled' || event.status === 'active' || event.status === 'ending';
  const canEdit = canCancel; // same rule - anything not ended/cancelled

  function handleCancel() {
    if (!confirmingCancel) { setConfirmingCancel(true); return; }
    cancel.mutate(event.id, { onSuccess: () => setConfirmingCancel(false) });
  }

  return (
    <div className="clan-event-card">
      <button type="button" className="clan-event-card__summary" onClick={() => setExpanded(!expanded)}>
        <div>
          <p className="clan-event-card__clan">{event.clan_name}</p>
          <p className="clan-event-card__meta">{event.type === 'combined' ? 'XP + Boss' : event.type === 'xp' ? 'XP' : 'Boss'}</p>
        </div>
        {statusBadge(event.status)}
      </button>

      {expanded && !editing && (
        <div className="clan-event-card__detail">
          <p><strong>Channel:</strong> {event.channel_id}</p>
          <p><strong>{new Date(event.starts_at).getTime() <= Date.now() ? 'Started' : 'Starts'}:</strong> {formatExactDateTime(event.starts_at)} ({formatRelativeEither(event.starts_at)})</p>
          <p><strong>{new Date(event.ends_at).getTime() <= Date.now() ? 'Ended' : 'Ends'}:</strong> {formatExactDateTime(event.ends_at)} ({formatRelativeEither(event.ends_at)})</p>
          {event.skills && event.skills.length > 0 && <p><strong>Skills:</strong> {event.skills.join(', ')}</p>}
          {event.categories && event.categories.length > 0 && <p><strong>Categories:</strong> {event.categories.join(', ')}</p>}
          <p><strong>Prizes:</strong> {event.prize_pool?.length ? event.prize_pool.map((p) => `#${p.place} ${p.prize}`).join(', ') : 'None'}</p>
          <p><strong>Raffle:</strong> {event.raffle_enabled ? 'Enabled' : 'Disabled'}</p>

          <div className="clan-event-card__actions">
            {canEdit && <button className="button-secondary" onClick={() => setEditing(true)}>Edit</button>}
            {canCancel && (
              <button className="button-danger" onClick={handleCancel} disabled={cancel.isPending}>
                {confirmingCancel ? 'Click again to confirm' : 'Cancel event'}
              </button>
            )}
          </div>
        </div>
      )}

      {expanded && editing && (
        <EventEditForm event={event} guildId={guildId} onDone={() => setEditing(false)} />
      )}
    </div>
  );
}

function EventColumn({ title, events, guildId, emptyText }: { title: string; events: ClanEvent[]; guildId: string | null; emptyText: string }) {
  return (
    <article className="card scrollable-card">
      <header className="card__header">
        <div>
          <p className="card__eyebrow">{events.length}</p>
          <h2 className="card__title">{title}</h2>
        </div>
      </header>
      <div className="scrollable-card__scroll clan-events-column">
        {events.length === 0
          ? <p style={{ color: 'var(--text-muted)', fontSize: 13, padding: '0 1.25rem' }}>{emptyText}</p>
          : events.map((event) => <EventCard key={event.id} event={event} guildId={guildId} />)}
      </div>
    </article>
  );
}

// Experimental - a first pass at showing raw tracked snapshot data
// for verification purposes (design confirmed 2026-07-16). Only
// active/ended events have any snapshot at all; only ended events
// have an ending snapshot too.
function SnapshotViewerCard({ events, guildId }: { events: ClanEvent[]; guildId: string | null }) {
  const eligible = events.filter((e) => e.status === 'active' || e.status === 'ending' || e.status === 'ended');
  const [selectedId, setSelectedId] = useState<number | null>(eligible[0]?.id ?? null);
  const [view, setView] = useState<'start' | 'end'>('start');

  const selectedEvent = eligible.find((e) => e.id === selectedId) ?? null;
  const { data: snapshot, isLoading } = useEventSnapshot(guildId, selectedId);

  if (eligible.length === 0) {
    return (
      <article className="card">
        <header className="card__header"><h2 className="card__title">Snapshot Viewer</h2></header>
        <div className="card__content">
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No active or finished events to inspect yet.</p>
        </div>
      </article>
    );
  }

  const canViewEnd = selectedEvent?.status === 'ended';
  const activeData = view === 'start'
    ? { xp: snapshot?.xp_snapshot_start, boss: snapshot?.boss_snapshot_start }
    : { xp: snapshot?.xp_snapshot_end, boss: snapshot?.boss_snapshot_end };

  // {} is truthy in JS - a present-but-empty snapshot object would
  // otherwise still render an empty block. Also used for the new
  // late-joiners section below.
  function hasData(obj: Record<string, unknown> | null | undefined): obj is Record<string, unknown> {
    return !!obj && Object.keys(obj).length > 0;
  }

  const hasLateJoiners = view === 'start' && (hasData(snapshot?.xp_late_joiners) || hasData(snapshot?.boss_late_joiners));

  return (
    <article className="card">
      <header className="card__header">
        <div>
          <p className="card__eyebrow">Verification / testing</p>
          <h2 className="card__title">Snapshot Viewer</h2>
        </div>
      </header>
      <div className="card__content">
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <select
            className="input"
            value={selectedId ?? ''}
            onChange={(e) => { setSelectedId(Number(e.target.value)); setView('start'); }}
            style={{ minWidth: 220 }}
          >
            {eligible.map((e) => (
              <option key={e.id} value={e.id}>{e.clan_name} ({e.status}) — #{e.id}</option>
            ))}
          </select>

          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <button className={view === 'start' ? 'button-primary' : 'button-secondary'} onClick={() => setView('start')}>
              Starting snapshot
            </button>
            <button
              className={view === 'end' ? 'button-primary' : 'button-secondary'}
              onClick={() => canViewEnd && setView('end')}
              disabled={!canViewEnd}
              title={!canViewEnd ? 'Only available once this event has ended' : undefined}
            >
              Ending snapshot
            </button>
          </div>
        </div>

        {isLoading && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</p>}

        {!isLoading && snapshot && (
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            {hasData(activeData.xp) && (
              <div style={{ flex: 1, minWidth: 260 }}>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 0.4rem' }}>XP data</p>
                <pre className="snapshot-viewer__json">{JSON.stringify(activeData.xp, null, 2)}</pre>
              </div>
            )}
            {hasData(activeData.boss) && (
              <div style={{ flex: 1, minWidth: 260 }}>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 0.4rem' }}>Boss kill data</p>
                <pre className="snapshot-viewer__json">{JSON.stringify(activeData.boss, null, 2)}</pre>
              </div>
            )}
            {!hasData(activeData.xp) && !hasData(activeData.boss) && (
              <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No data recorded for this snapshot.</p>
            )}
          </div>
        )}

        {!isLoading && hasLateJoiners && (
          <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid var(--border-subtle)' }}>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 0.6rem' }}>
              Late joiners — baseline captured when each was detected joining, not at the event&apos;s
              actual start time.
            </p>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              {hasData(snapshot?.xp_late_joiners) && (
                <div style={{ flex: 1, minWidth: 260 }}>
                  <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 0.4rem' }}>XP late-joiner baselines</p>
                  <pre className="snapshot-viewer__json">{JSON.stringify(snapshot?.xp_late_joiners, null, 2)}</pre>
                </div>
              )}
              {hasData(snapshot?.boss_late_joiners) && (
                <div style={{ flex: 1, minWidth: 260 }}>
                  <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 0.4rem' }}>Boss late-joiner baselines</p>
                  <pre className="snapshot-viewer__json">{JSON.stringify(snapshot?.boss_late_joiners, null, 2)}</pre>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

export function ClanEventsPage() {
  const { guilds, selectedGuildId } = useGuild();
  const guild = guilds.find((g) => g.guild_id === selectedGuildId);
  const { data: events, isLoading, error } = useClanEvents(selectedGuildId);

  const active = (events ?? []).filter((e) => e.status === 'active' || e.status === 'ending');
  const planned = (events ?? []).filter((e) => e.status === 'scheduled');
  const finished = (events ?? []).filter((e) => e.status === 'ended' || e.status === 'cancelled');

  return (
    <>
      <div className="page-header">
        <p className="page-header__eyebrow">{guild ? formatGuildName(guild.guild_name, guild.guild_id) : 'No server selected'}</p>
        <h1 className="page-header__title">Clan Events</h1>
      </div>

      {isLoading && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</p>}
      {error && <p style={{ color: 'var(--negative)', fontSize: 13 }}>Couldn&apos;t load clan events.</p>}

      {events && (
        <div className="clan-events-columns">
          <EventColumn title="Active" events={active} guildId={selectedGuildId} emptyText="No events currently running." />
          <EventColumn title="Planned" events={planned} guildId={selectedGuildId} emptyText="No upcoming events." />
          <EventColumn title="Finished" events={finished} guildId={selectedGuildId} emptyText="No past events yet." />
        </div>
      )}

      {events && (
        <div className="clan-events-second-grid">
          <SnapshotViewerCard events={events} guildId={selectedGuildId} />
        </div>
      )}
    </>
  );
}
